import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

const API_URL = window.location.hostname === 'tg-bypadel.ncar.club'
  ? 'https://api-tg-bypadel.ncar.club/api'
  : `${window.location.origin}/api`;
const tg = window.Telegram?.WebApp;

tg?.ready?.();
tg?.expand?.();

function displayName(info) {
  const full = [info?.first_name, info?.last_name].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (info?.username) return `@${info.username}`;
  return 'Без имени';
}

async function api(path, options = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(text.includes('<html') ? 'API вернул HTML вместо JSON. Проверьте домен API.' : 'Некорректный ответ сервера');
  }
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.detail || data.error || 'Ошибка запроса');
  }
  return data;
}

function useInterval(callback, delay) {
  const saved = useRef(callback);
  useEffect(() => { saved.current = callback; }, [callback]);
  useEffect(() => {
    if (delay == null) return;
    const id = setInterval(() => saved.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

function App() {
  const [viewer, setViewer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const login = useCallback(async () => {
    if (!tg) throw new Error('Откройте WebApp из Telegram');
    let initData = (tg.initData || '').trim();
    if (!initData) throw new Error('Не удалось получить данные Telegram');
    const data = await api('/login', { method: 'POST', body: JSON.stringify({ initData }) });
    if (!data.authenticated) throw new Error(data.error || 'Нет доступа');
    setViewer(data.user);
    tg.expand?.();
    tg.enableClosingConfirmation?.();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await login();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [login]);

  if (loading) return <div className="screen-center"><div className="spinner" /><p>Загрузка панели...</p></div>;
  if (!viewer) {
    return <div className="screen-center"><div className="card login-card"><h1>BY Padel CRM</h1><p className="muted">Откройте WebApp из Telegram.</p>{error && <div className="error-box">{error}</div>}</div></div>;
  }
  return <Dashboard viewer={viewer} />;
}

function Dashboard({ viewer }) {
  const [typeFilter, setTypeFilter] = useState(viewer.role === 'manager' ? 'manager' : 'all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [activeChat, setActiveChat] = useState(null);
  const [loadingChats, setLoadingChats] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [form, setForm] = useState({ first_name:'', last_name:'', username:'', phone:'', city:'', courts_count:'' });
  const listRef = useRef(null);
  const messagesRef = useRef(null);
  const listScrollRef = useRef(0);
  const nearBottomRef = useRef(true);

  const fetchChats = useCallback(async (silent = true) => {
    if (listRef.current) listScrollRef.current = listRef.current.scrollTop;
    if (!silent) setLoadingChats(true);
    try {
      const data = await api(`/chats?viewer_telegram_id=${viewer.telegram_id}&chat_type=${typeFilter}&status=${statusFilter}`);
      setChats(data.items || []);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      if (!silent) setLoadingChats(false);
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = listScrollRef.current;
      });
    }
  }, [viewer.telegram_id, typeFilter, statusFilter]);

  const fetchChat = useCallback(async (chatId, forceBottom = false) => {
    if (!chatId) return;
    const box = messagesRef.current;
    if (box) nearBottomRef.current = box.scrollHeight - box.scrollTop - box.clientHeight < 120;
    try {
      const data = await api(`/chats/${chatId}?viewer_telegram_id=${viewer.telegram_id}`);
      setActiveChat(data);
      await api(`/chats/${chatId}/read?viewer_telegram_id=${viewer.telegram_id}`, { method: 'POST' });
      setError('');
      requestAnimationFrame(() => {
        if (!messagesRef.current) return;
        if (forceBottom || nearBottomRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      });
    } catch (e) {
      setError(e.message);
    }
  }, [viewer.telegram_id]);

  useEffect(() => { fetchChats(false); }, [fetchChats]);
  useEffect(() => {
    if (!selectedChatId) {
      setActiveChat(null);
      return;
    }
    fetchChat(selectedChatId, true);
  }, [selectedChatId, fetchChat]);
  useInterval(() => fetchChats(true), 12000);
  useInterval(() => { if (selectedChatId) fetchChat(selectedChatId, false); }, selectedChatId ? 3000 : null);

  const selectedChatMeta = useMemo(() => chats.find((c) => c.id === selectedChatId), [chats, selectedChatId]);

  const openEditor = () => {
    if (!activeChat?.client) return;
    setForm({
      first_name: activeChat.client.first_name || '',
      last_name: activeChat.client.last_name || '',
      username: activeChat.client.username || '',
      phone: activeChat.client.phone || activeChat.survey?.phone || '',
      city: activeChat.client.city || activeChat.survey?.city || '',
      courts_count: activeChat.client.courts_count || activeChat.survey?.courts_count || '',
    });
    setShowEditor(true);
  };

  const saveClient = async () => {
    try {
      await api(`/chats/${selectedChatId}/client`, {
        method: 'PATCH',
        body: JSON.stringify({
          viewer_telegram_id: viewer.telegram_id,
          first_name: form.first_name,
          last_name: form.last_name,
          username: form.username,
          phone: form.phone,
          city: form.city,
          courts_count: form.courts_count ? Number(form.courts_count) : null,
        }),
      });
      setShowEditor(false);
      await fetchChat(selectedChatId, false);
      await fetchChats(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !selectedChatId || sending) return;
    setSending(true);
    try {
      await api(`/chats/${selectedChatId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ viewer_telegram_id: viewer.telegram_id, text: message.trim() }),
      });
      setMessage('');
      await fetchChat(selectedChatId, true);
      await fetchChats(true);
    } catch (e) {
      setError(e.message);
      await fetchChats(true);
      await fetchChat(selectedChatId, false);
    } finally {
      setSending(false);
    }
  };

  const listScreen = (
    <div className="list-screen card">
      <div className="sidebar-header">
        <h1>Чаты BY Padel</h1>
        <p className="muted">{displayName(viewer)} · {viewer.role === 'admin' ? 'Администратор' : 'Менеджер'}</p>
      </div>
      <div className="filters block-gap">
        {['active','inactive','all'].map((value) => (
          <button key={value} className={`chip ${statusFilter === value ? 'active' : ''}`} onClick={() => setStatusFilter(value)}>
            {value === 'active' ? 'Активные' : value === 'inactive' ? 'Неактивные' : 'Все'}
          </button>
        ))}
      </div>
      {viewer.role === 'admin' && (
        <div className="filters block-gap top-tight">
          {['all','manager','head'].map((value) => (
            <button key={value} className={`chip ${typeFilter === value ? 'active' : ''}`} onClick={() => setTypeFilter(value)}>
              {value === 'all' ? 'Все чаты' : value === 'manager' ? 'Менеджер' : 'Руководитель'}
            </button>
          ))}
        </div>
      )}
      <div className="chat-list" ref={listRef}>
        {loadingChats ? <div className="muted">Загрузка чатов...</div> : chats.map((chat) => (
          <button key={chat.id} className="chat-item" onClick={() => setSelectedChatId(chat.id)}>
            <div className="chat-item-top">
              <span className="chat-name">{displayName(chat.user_info)}</span>
              <span className={`badge ${chat.is_active ? '' : 'badge-muted'}`}>{chat.is_active ? 'Активен' : 'Закрыт'}</span>
            </div>
            <div className="chat-item-sub">{viewer.role === 'admin' ? chat.chat_type_label : chat.user_info.lead_status}</div>
            <div className="chat-item-sub clamp">{chat.last_message_preview || 'Нет сообщений'}</div>
            {chat.unread_count ? <div className="unread">Непрочитано: {chat.unread_count}</div> : null}
          </button>
        ))}
      </div>
    </div>
  );

  const detailScreen = (
    <main className="chat-panel card detail-screen">
      <div className="detail-topbar">
        <button className="secondary-btn back-btn" onClick={() => setSelectedChatId(null)}>← К чатам</button>
        <div className="muted">{selectedChatMeta?.chat_type_label || ''}</div>
      </div>
      {!selectedChatId || !activeChat ? <div className="empty-state">Выберите чат</div> : <>
        <div className="client-card">
          <div className="client-head">
            <div>
              <div className="client-name">{[activeChat.client.first_name, activeChat.client.last_name].filter(Boolean).join(' ') || 'Без имени'}</div>
              <div className="muted">{activeChat.client.username ? `@${activeChat.client.username}` : 'username не указан'}</div>
            </div>
            <button className="secondary-btn" onClick={openEditor}>✏️ Редактировать</button>
          </div>
          <div className="client-grid">
            <div><span className="label">📞 Телефон</span><strong>{activeChat.client.phone || activeChat.survey?.phone || 'не указан'}</strong></div>
            <div><span className="label">📍 Город</span><strong>{activeChat.client.city || activeChat.survey?.city || 'не указан'}</strong></div>
            <div><span className="label">🏟 Количество кортов</span><strong>{activeChat.client.courts_count || activeChat.survey?.courts_count || 'не указано'}</strong></div>
          </div>
          <div className="client-foot">
            <span>{activeChat.client.lead_status}</span>
            <span>Последняя активность: {activeChat.client.last_activity_at || '—'}</span>
          </div>
        </div>
        <div className="messages" ref={messagesRef} onScroll={() => {
          const node = messagesRef.current;
          if (node) nearBottomRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 120;
        }}>
          {activeChat.messages.map((msg) => (
            <div key={msg.id} className={`bubble ${msg.is_from_user ? 'user' : 'staff'}`}>
              <div className="bubble-head">{msg.is_from_user ? '👤 Пользователь' : msg.sender_label === 'Менеджер' ? '💼 Вы (Менеджер)' : '👔 Вы (Руководитель)'}</div>
              <div className="bubble-text">{msg.text}</div>
              <div className="bubble-time">{msg.created_at}</div>
            </div>
          ))}
        </div>
        <div className="composer">
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder={`Ответ в чат ${selectedChatMeta?.chat_type_label?.toLowerCase() || ''}`} />
          <button onClick={sendMessage} disabled={sending}>{sending ? 'Отправка...' : 'Отправить'}</button>
        </div>
      </>}
      {error ? <div className="error-box floating">{error}</div> : null}
    </main>
  );

  return (
    <div className="single-layout">
      {!selectedChatId ? listScreen : detailScreen}
      {showEditor && (
        <div className="modal-overlay" onClick={() => setShowEditor(false)}>
          <div className="modal card" onClick={(e) => e.stopPropagation()}>
            <h2>Редактировать данные</h2>
            <div className="form-grid">
              <label>Имя<input value={form.first_name} onChange={(e) => setForm((s) => ({ ...s, first_name: e.target.value }))} /></label>
              <label>Фамилия<input value={form.last_name} onChange={(e) => setForm((s) => ({ ...s, last_name: e.target.value }))} /></label>
              <label>Username<input value={form.username} onChange={(e) => setForm((s) => ({ ...s, username: e.target.value }))} /></label>
              <label>Телефон<input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} /></label>
              <label>Город<input value={form.city} onChange={(e) => setForm((s) => ({ ...s, city: e.target.value }))} /></label>
              <label>Количество кортов<input type="number" min="1" max="100" value={form.courts_count} onChange={(e) => setForm((s) => ({ ...s, courts_count: e.target.value }))} /></label>
            </div>
            <div className="modal-actions">
              <button className="secondary-btn" onClick={() => setShowEditor(false)}>Отмена</button>
              <button onClick={saveClient}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
